import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, X } from 'lucide-react';

export function BillingDeadlineNotification() {
  const { user, accessInfo, checkingAccess } = useAuth();
  const navigate = useNavigate();
  const [showNotification, setShowNotification] = useState(false);
  const [daysRemaining, setDaysRemaining] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [isTrial, setIsTrial] = useState(false);

  useEffect(() => {
    // PERFORMANCE FIX: Defer check by 2 seconds to avoid connection pool exhaustion
    // Billing notification is not critical for initial page load
    const timer = setTimeout(() => {
      checkBillingDeadline();
    }, 2000);
    return () => clearTimeout(timer);
  }, [user, accessInfo, checkingAccess]);

  const checkBillingDeadline = () => {
    if (!user || dismissed || checkingAccess || !accessInfo) return;

    const dismissedKey = `billing_notification_dismissed_${user.id}`;
    if (localStorage.getItem(dismissedKey)) {
      setDismissed(true);
      return;
    }

    try {
      // Use cached access info from context - NO DATABASE QUERY!
      if (
        (accessInfo.access_type === 'grace_period' || accessInfo.access_type === 'trial') &&
        accessInfo.subscription_end_date
      ) {
        const endDate = new Date(accessInfo.subscription_end_date);
        const now = new Date();
        const diffTime = endDate.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        const maxDays = accessInfo.access_type === 'trial' ? 5 : 90;
        if (diffDays > 0 && diffDays <= maxDays) {
          setDaysRemaining(diffDays);
          setIsTrial(accessInfo.access_type === 'trial');
          setShowNotification(true);
        }
      }
    } catch (error) {
      console.error('Error checking billing deadline:', error);
    }
  };

  const handleDismiss = () => {
    setShowNotification(false);
    setDismissed(true);
    if (user) {
      localStorage.setItem(`billing_notification_dismissed_${user.id}`, 'true');
    }
  };

  const handleViewPlans = () => {
    navigate('/app/paywall');
  };

  if (!showNotification || dismissed) {
    return null;
  }

  const isUrgent = isTrial ? daysRemaining <= 2 : daysRemaining <= 30;
  const deadline = new Date('2026-01-01');
  const formattedDeadline = deadline.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const title = isTrial
    ? `${isUrgent ? 'Action Required: ' : ''}Free Trial`
    : `${isUrgent ? 'Action Required: ' : ''}Subscription Required Starting ${formattedDeadline}`;

  const message = isTrial
    ? `You have ${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'} remaining in your free trial. Subscribe now to continue enjoying full access to all features.`
    : `You have ${daysRemaining} days of free access remaining until ${formattedDeadline}. Choose a plan now to ensure uninterrupted access—your subscription will begin after your free period ends, so you won't lose any free days.`;

  return (
    <div
      className={`fixed top-20 left-1/2 transform -translate-x-1/2 z-[60] max-w-2xl w-full mx-4 ${
        isUrgent
          ? 'bg-orange-50 dark:bg-orange-900/20 border-2 border-orange-500'
          : 'bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-500'
      } rounded-lg shadow-lg p-4`}
    >
      <div className="flex items-start space-x-3">
        <AlertCircle
          className={`w-6 h-6 flex-shrink-0 mt-0.5 ${
            isUrgent ? 'text-orange-600 dark:text-orange-400' : 'text-blue-600 dark:text-blue-400'
          }`}
        />
        <div className="flex-1">
          <h3
            className={`font-semibold mb-1 ${
              isUrgent
                ? 'text-orange-900 dark:text-orange-100'
                : 'text-blue-900 dark:text-blue-100'
            }`}
          >
            {title}
          </h3>
          <p
            className={`text-sm mb-3 ${
              isUrgent
                ? 'text-orange-800 dark:text-orange-200'
                : 'text-blue-800 dark:text-blue-200'
            }`}
          >
            {message}
          </p>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleViewPlans}
              className={`text-sm font-medium px-4 py-2 rounded-lg transition-colors ${
                isUrgent
                  ? 'bg-orange-600 hover:bg-orange-700 text-white'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              View Plans
            </button>
            <button
              onClick={handleDismiss}
              className={`text-sm font-medium ${
                isUrgent
                  ? 'text-orange-700 dark:text-orange-300 hover:text-orange-900 dark:hover:text-orange-100'
                  : 'text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-100'
              }`}
            >
              Remind me later
            </button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className={`flex-shrink-0 ${
            isUrgent
              ? 'text-orange-600 dark:text-orange-400 hover:text-orange-900 dark:hover:text-orange-100'
              : 'text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-100'
          }`}
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
